import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { AngularMaterialModule } from '../../angular-material.module';
import { TeamFlagService } from '../../shared/team-flag/team-flag.service';
import { GymLocation } from '../../gym-locations/gym-location.model';
import { PlayoffBracket, PlayoffMatchup } from '../playoff.model';

export interface MatchupScheduleUpdate {
  matchupId: string;
  game_date: string | null;
  game_time: string | null;
  venue_court: string | null;
  venue_school_name: string | null;
  venue_address: string | null;
}

export interface PlayoffConnectorPair {
  sourceMatchupIds: [string, string];
  sourceRound: number;
  sourcePositions: [number, number];
  destinationMatchupId: string;
  destinationRound: number;
  destinationPosition: number;
}

export interface PlayoffConnectorGeometry {
  sourceRight: [number, number];
  sourceCenterY: [number, number];
  destinationLeft: number;
  destinationCenterY: number;
}

interface PlayoffConnectorPath extends PlayoffConnectorPair {
  key: string;
  d: string;
}

@Component({
  selector: 'app-playoff-bracket',
  standalone: true,
  imports: [CommonModule, FormsModule, AngularMaterialModule],
  templateUrl: './playoff-bracket.component.html',
  styleUrls: ['./playoff-bracket.component.css'],
})
export class PlayoffBracketComponent implements AfterViewInit, OnChanges, OnDestroy {
  @Input() bracket: PlayoffBracket | null = null;
  @Input() editable = false;
  @Input() showSeeds = true;
  @Input() savingMatchupId: string | null = null;
  @Input() gymLocations: GymLocation[] = [];
  @Input() teamLogoUrls: Record<string, string | null> = {};
  @Output() scoreSubmit = new EventEmitter<{ matchupId: string; homeScore: number; awayScore: number }>();
  @Output() scoreReset = new EventEmitter<{ matchupId: string }>();
  @Output() scheduleSubmit = new EventEmitter<MatchupScheduleUpdate>();

  editingMatchupId: string | null = null;
  editHome: number | null = null;
  editAway: number | null = null;

  schedulingMatchupId: string | null = null;
  schedDate = '';
  schedTime = '';
  schedCourt = '';
  connectorPaths: PlayoffConnectorPath[] = [];
  @ViewChild('bracketElement') private bracketElement?: ElementRef<HTMLElement>;

  private readonly brokenFlagNames = new Set<string>();
  private readonly brokenLogoTeamIds = new Set<string>();
  private resizeObserver: ResizeObserver | null = null;
  private readonly observedLayoutElements = new Set<HTMLElement>();
  private refreshTimer: ReturnType<typeof setTimeout> | null = null;
  private destroyed = false;

  constructor(private flags: TeamFlagService) {}

  ngAfterViewInit(): void {
    this.observeBracketLayout();
    this.scheduleConnectorRefresh();
  }

  ngOnChanges(): void {
    this.scheduleConnectorRefresh();
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    if (this.refreshTimer !== null) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = null;
    }
    this.resizeObserver?.disconnect();
    this.resizeObserver = null;
    this.observedLayoutElements.clear();
  }

  @HostListener('window:scroll')
  onWindowScroll(): void {
    this.scheduleConnectorRefresh();
  }

  onBracketScroll(): void {
    this.scheduleConnectorRefresh();
  }

  /** Matchups grouped into columns by round, each sorted by position. */
  get rounds(): PlayoffMatchup[][] {
    if (!this.bracket) return [];
    const byRound = new Map<number, PlayoffMatchup[]>();
    for (const m of this.bracket.matchups) {
      if (!byRound.has(m.round)) byRound.set(m.round, []);
      byRound.get(m.round)!.push(m);
    }
    return [...byRound.keys()]
      .sort((a, b) => a - b)
      .map((r) => byRound.get(r)!.sort((a, b) => a.position - b.position));
  }

  /**
   * Resolves the stable 0-based bracket relationship used by connector paths.
   * A source pair at positions 0/1, 2/3, etc. feeds floor(position / 2) in
   * the immediately following numeric round.
   */
  static buildConnectorPairs(rounds: PlayoffMatchup[][]): PlayoffConnectorPair[] {
    const pairs: PlayoffConnectorPair[] = [];

    for (let roundIndex = 0; roundIndex < rounds.length - 1; roundIndex += 1) {
      const sourceRound = rounds[roundIndex];
      const destinationRound = rounds[roundIndex + 1];
      const sourceRoundNumber = sourceRound[0]?.round;
      const destinationRoundNumber = destinationRound[0]?.round;

      if (
        typeof sourceRoundNumber !== 'number' ||
        typeof destinationRoundNumber !== 'number' ||
        !Number.isFinite(sourceRoundNumber) ||
        !Number.isFinite(destinationRoundNumber) ||
        destinationRoundNumber !== sourceRoundNumber + 1
      ) {
        continue;
      }

      for (const source of sourceRound) {
        if (!Number.isInteger(source.position) || source.position < 0 || source.position % 2 !== 0) {
          continue;
        }

        const pairedSource = sourceRound.find((candidate) => candidate.position === source.position + 1);
        const destinationPosition = Math.floor(source.position / 2);
        const destination = destinationRound.find((candidate) => candidate.position === destinationPosition);

        if (!pairedSource || !destination) {
          continue;
        }

        pairs.push({
          sourceMatchupIds: [source.id, pairedSource.id],
          sourceRound: sourceRoundNumber,
          sourcePositions: [source.position, pairedSource.position],
          destinationMatchupId: destination.id,
          destinationRound: destinationRoundNumber,
          destinationPosition,
        });
      }
    }

    return pairs;
  }

  static buildConnectorPath(geometry: PlayoffConnectorGeometry): string | null {
    const values = [
      ...geometry.sourceRight,
      ...geometry.sourceCenterY,
      geometry.destinationLeft,
      geometry.destinationCenterY,
    ];
    if (values.some((value) => !Number.isFinite(value))) return null;

    const sourceRight = Math.max(...geometry.sourceRight);
    const destinationLeft = geometry.destinationLeft;
    if (destinationLeft <= sourceRight) return null;

    const trunkX = sourceRight + (destinationLeft - sourceRight) / 2;
    const trunkTop = Math.min(...geometry.sourceCenterY);
    const trunkBottom = Math.max(...geometry.sourceCenterY);
    const sourceMidpoint = (geometry.sourceCenterY[0] + geometry.sourceCenterY[1]) / 2;
    const format = (value: number): string => Number(value.toFixed(2)).toString();

    return [
      `M ${format(geometry.sourceRight[0])} ${format(geometry.sourceCenterY[0])} H ${format(trunkX)}`,
      `M ${format(geometry.sourceRight[1])} ${format(geometry.sourceCenterY[1])} H ${format(trunkX)}`,
      `M ${format(trunkX)} ${format(trunkTop)} V ${format(trunkBottom)}`,
      `M ${format(trunkX)} ${format(sourceMidpoint)} V ${format(geometry.destinationCenterY)} H ${format(destinationLeft)}`,
    ].join(' ');
  }

  roundLabel(round: number): string {
    if (!this.bracket) return '';
    const teamsInRound = this.bracket.size / Math.pow(2, round - 1);
    if (teamsInRound === 2) return 'Final';
    if (teamsInRound === 4) return 'Semifinals';
    if (teamsInRound === 8) return 'Quarterfinals';
    return `Round of ${teamsInRound}`;
  }

  flagImageUrl(name: string | null): string | null {
    if (!name || this.brokenFlagNames.has(this.flagKey(name))) {
      return null;
    }
    return this.flags.getFlagUrl(name);
  }

  teamLogoUrl(teamId: string | null): string | null {
    if (!teamId || this.brokenLogoTeamIds.has(teamId)) return null;
    return this.teamLogoUrls[teamId] || null;
  }

  markLogoBroken(teamId: string | null): void {
    if (teamId) this.brokenLogoTeamIds.add(teamId);
  }

  flagEmoji(name: string | null): string | null {
    return this.flags.getFlagEmoji(name);
  }

  markFlagBroken(name: string | null): void {
    if (name) {
      this.brokenFlagNames.add(this.flagKey(name));
    }
  }

  monogram(name: string | null): string {
    return (String(name || '?').trim()[0] || '?').toUpperCase();
  }

  private flagKey(name: string): string {
    return name.trim().toLowerCase();
  }

  isWinner(m: PlayoffMatchup, side: 'home' | 'away'): boolean {
    if (!m.winner_team_id) return false;
    const id = side === 'home' ? m.home_team_id : m.away_team_id;
    return !!id && id === m.winner_team_id;
  }

  isLiveMatchup(m: PlayoffMatchup): boolean {
    return m.is_live === true;
  }

  livePeriodLabel(m: PlayoffMatchup): string {
    const period = String(m.period || '').toUpperCase();
    if (period === 'H1') return '1st Half';
    if (period === 'H2') return '2nd Half';
    if (/^Q[1-4]$/.test(period)) return `Q${period.slice(1)}`;
    if (period === 'OT') return 'OT 1';
    if (/^OT\d+$/.test(period)) return `OT ${period.slice(2)}`;
    return 'In progress';
  }

  liveClock(m: PlayoffMatchup): string {
    return /^\d{1,2}:[0-5]\d$/.test(String(m.game_clock || ''))
      ? String(m.game_clock)
      : '--:--';
  }

  canEditMatchup(m: PlayoffMatchup): boolean {
    return this.editable && !this.isScheduleLocked(m) && !m.is_bye && !!m.home_team_id && !!m.away_team_id;
  }

  startEdit(m: PlayoffMatchup): void {
    if (!this.canEditMatchup(m)) return;
    this.editingMatchupId = m.id;
    this.editHome = m.home_score;
    this.editAway = m.away_score;
    this.scheduleConnectorRefresh();
  }

  cancelEdit(): void {
    this.editingMatchupId = null;
    this.editHome = null;
    this.editAway = null;
    this.scheduleConnectorRefresh();
  }

  submitEdit(m: PlayoffMatchup): void {
    if (this.editHome === null || this.editAway === null) return;
    if (Number(this.editHome) === Number(this.editAway)) return;
    this.scoreSubmit.emit({
      matchupId: m.id,
      homeScore: Number(this.editHome),
      awayScore: Number(this.editAway),
    });
    this.cancelEdit();
  }

  resetScore(m: PlayoffMatchup): void {
    if (!this.editable || this.isScheduleLocked(m) || (!m.winner_team_id && m.home_score === null && m.away_score === null)) return;
    if (!window.confirm('Reset this score and clear its advancement?')) return;
    this.scoreReset.emit({ matchupId: m.id });
  }

  // ── Date & court ──────────────────────────────────────────────────────────
  hasSchedule(m: PlayoffMatchup): boolean {
    return !!(m.game_date || m.game_time || m.venue_court);
  }

  scheduleLabel(m: PlayoffMatchup): string {
    const parts: string[] = [];
    if (m.game_date) parts.push(this.formatDate(m.game_date));
    if (m.game_time) parts.push(this.formatTime(m.game_time));
    if (m.venue_court) parts.push(m.venue_court);
    return parts.join(' · ');
  }

  formatDate(d: string): string {
    const dt = new Date(d.length <= 10 ? d + 'T12:00:00' : d);
    return isNaN(dt.getTime()) ? d : dt.toLocaleDateString('en-CA', { month: 'short', day: 'numeric' });
  }

  formatTime(t: string): string {
    const [h, m] = String(t).split(':');
    const hour = Number(h);
    if (!Number.isFinite(hour)) return t;
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const h12 = hour % 12 === 0 ? 12 : hour % 12;
    return `${h12}:${(m || '00').padStart(2, '0')} ${ampm}`;
  }

  canScheduleMatchup(m: PlayoffMatchup): boolean {
    // Later rounds need scheduling before their winners are known.
    return this.editable && (!m.is_bye || m.round >= 2);
  }

  isScheduleLocked(m: PlayoffMatchup): boolean {
    return m.schedule_locked === true;
  }

  startSchedule(m: PlayoffMatchup): void {
    if (!this.canScheduleMatchup(m)) return;
    this.schedulingMatchupId = m.id;
    this.schedDate = this.dateInputValue(m.game_date);
    this.schedTime = m.game_time || '';
    this.schedCourt = m.venue_court || '';
    this.scheduleConnectorRefresh();
  }

  cancelSchedule(): void {
    this.schedulingMatchupId = null;
    this.schedDate = '';
    this.schedTime = '';
    this.schedCourt = '';
    this.scheduleConnectorRefresh();
  }

  submitSchedule(m: PlayoffMatchup): void {
    const gym = this.gymLocations.find((g) => g.court_name === this.schedCourt);
    this.scheduleSubmit.emit({
      matchupId: m.id,
      game_date: this.schedDate || null,
      game_time: this.schedTime || null,
      venue_court: this.schedCourt || null,
      venue_school_name: gym?.location_name || null,
      venue_address: gym?.address || null,
    });
    this.cancelSchedule();
  }

  private scheduleConnectorRefresh(): void {
    if (this.destroyed || this.refreshTimer !== null) return;

    this.refreshTimer = setTimeout(() => {
      this.refreshTimer = null;
      this.refreshConnectorGeometry();
    }, 0);
  }

  private observeBracketLayout(): void {
    if (typeof ResizeObserver === 'undefined' || !this.bracketElement) return;

    if (!this.resizeObserver) {
      this.resizeObserver = new ResizeObserver(() => this.scheduleConnectorRefresh());
    }

    const nextElements = new Set<HTMLElement>([
      this.bracketElement.nativeElement,
      ...Array.from(this.bracketElement.nativeElement.querySelectorAll<HTMLElement>('.matchup')),
    ]);

    for (const element of Array.from(this.observedLayoutElements)) {
      if (!nextElements.has(element)) {
        this.resizeObserver.unobserve(element);
        this.observedLayoutElements.delete(element);
      }
    }

    for (const element of nextElements) {
      if (!this.observedLayoutElements.has(element)) {
        this.resizeObserver.observe(element);
        this.observedLayoutElements.add(element);
      }
    }
  }

  private refreshConnectorGeometry(): void {
    const bracket = this.bracketElement?.nativeElement;
    if (!bracket) {
      if (this.resizeObserver) {
        for (const element of Array.from(this.observedLayoutElements)) {
          this.resizeObserver.unobserve(element);
        }
        this.observedLayoutElements.clear();
      }
      this.connectorPaths = [];
      return;
    }

    this.observeBracketLayout();
    const bracketRect = bracket.getBoundingClientRect();
    const matchupElements = Array.from(bracket.querySelectorAll<HTMLElement>('[data-matchup-id]'));
    const matchupById = new Map(matchupElements.map((element) => [element.dataset['matchupId'] || '', element]));
    const paths: PlayoffConnectorPath[] = [];

    for (const pair of PlayoffBracketComponent.buildConnectorPairs(this.rounds)) {
      const sourceElements = pair.sourceMatchupIds.map((id) => matchupById.get(id));
      const destinationElement = matchupById.get(pair.destinationMatchupId);
      if (!sourceElements[0] || !sourceElements[1] || !destinationElement) continue;

      const sourceRects = sourceElements.map((element) => element.getBoundingClientRect());
      const destinationRect = destinationElement.getBoundingClientRect();
      if (
        sourceRects.some((rect) => rect.width <= 0 || rect.height <= 0) ||
        destinationRect.width <= 0 ||
        destinationRect.height <= 0
      ) {
        continue;
      }

      const sourceRight: [number, number] = [
        sourceRects[0].right - bracketRect.left,
        sourceRects[1].right - bracketRect.left,
      ];
      const sourceCenterY: [number, number] = [
        sourceRects[0].top - bracketRect.top + sourceRects[0].height / 2,
        sourceRects[1].top - bracketRect.top + sourceRects[1].height / 2,
      ];
      const destinationLeft = destinationRect.left - bracketRect.left;
      const destinationCenterY = destinationRect.top - bracketRect.top + destinationRect.height / 2;
      const d = PlayoffBracketComponent.buildConnectorPath({
        sourceRight,
        sourceCenterY,
        destinationLeft,
        destinationCenterY,
      });
      if (!d) continue;

      paths.push({
        ...pair,
        key: `${pair.sourceMatchupIds.join(':')}-${pair.destinationMatchupId}`,
        d,
      });
    }

    this.connectorPaths = paths;
  }

  private dateInputValue(value: string | null): string {
    if (!value) return '';
    const dateOnlyMatch = /^(\d{4}-\d{2}-\d{2})/.exec(value);
    if (dateOnlyMatch) {
      return dateOnlyMatch[1];
    }

    const date = new Date(value);
    if (isNaN(date.getTime())) {
      return value;
    }

    const local = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
    return local.toISOString().slice(0, 10);
  }
}
